import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useLayoutEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';

// The keyframes each of these drives live in globals.css.
type Direction = 'forward' | 'back' | 'fade' | 'fade-slow';
// `to` is a path to push, or a history delta (e.g. -1 to pop back along the path the user actually took).
type TransitionNavigate = (to: string | number, direction?: Direction, options?: NavigateOptions) => void;

type ViewTransitionDocument = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => { finished: Promise<void> };
};

const setDirection = (direction: Direction): void => {
  document.documentElement.dataset.vtDirection = direction;
};
const clearDirection = (direction: Direction): void => {
  // Only clear if it's still ours, so overlapping transitions don't wipe each other's direction.
  if (document.documentElement.dataset.vtDirection === direction) delete document.documentElement.dataset.vtDirection;
};

/** flushSync commits inside the callback so the browser can snapshot the new DOM; navigation cannot. */
export const runViewTransition = (direction: Direction, update: () => void): Promise<void> => {
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition) {
    update();
    return Promise.resolve();
  }
  setDirection(direction);
  const transition = doc.startViewTransition(() => flushSync(update));
  // Settles rather than rejects when skipped: the tidying is the same either way.
  return transition.finished.finally(() => clearDirection(direction)).catch(() => undefined);
};

const ViewTransitionNavContext = createContext<TransitionNavigate | null>(null);

/**
 * navigate() with a fade/swipe View Transition. We can't use RR's `viewTransition` Link prop — the app
 * runs on <BrowserRouter>, not a data router — and `flushSync(navigate)` doesn't force a synchronous
 * commit either (router updates go through React.startTransition via the `v7_startTransition` flag). So
 * we hold the transition open (an unresolved promise) until the new route commits, detected from the
 * pathname change. The provider must sit ABOVE everything that navigates (including the sidebar/logo) and
 * stay mounted across the navigation so its effect can resolve the pending transition.
 */
export const ViewTransitionNavProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resolveRef = useRef<(() => void) | null>(null);

  // The route has committed (pathname changed) — let the pending transition snapshot the new DOM.
  useLayoutEffect(() => {
    resolveRef.current?.();
    resolveRef.current = null;
  }, [pathname]);

  const transitionNavigate = useCallback<TransitionNavigate>(
    (to, direction, options) => {
      const dir = direction ?? 'forward';
      const doc = document as ViewTransitionDocument;
      const runNavigate = (): void => {
        if (typeof to === 'number') navigate(to);
        else navigate(to, options);
      };
      if (!doc.startViewTransition) {
        runNavigate();
        return;
      }
      setDirection(dir);
      const transition = doc.startViewTransition(
        () =>
          new Promise<void>(resolve => {
            resolveRef.current = resolve;
            runNavigate();
            // Safety net: if the route doesn't actually change, don't leave the transition hanging.
            window.setTimeout(() => {
              if (resolveRef.current === resolve) {
                resolveRef.current = null;
                resolve();
              }
            }, 500);
          }),
      );
      // Aborted when the viewport resizes mid-transition; the navigation itself has still happened.
      transition.finished.finally(() => clearDirection(dir)).catch(() => undefined);
    },
    [navigate],
  );

  return <ViewTransitionNavContext.Provider value={transitionNavigate}>{children}</ViewTransitionNavContext.Provider>;
};

// Navigate with a View Transition when one is available; falls back to a plain navigate otherwise.
export const useViewTransitionNav = (): TransitionNavigate => {
  const ctx = useContext(ViewTransitionNavContext);
  const navigate = useNavigate();
  return ctx ?? ((to, _direction, options) => (typeof to === 'number' ? navigate(to) : navigate(to, options)));
};
